import { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { sortArray } from '../../../utils/util';

import {
  requestViewAllClassifications,
  requestViewAllCategories,
  requestViewAllTypes,
} from '../../../api/product/product.actions';
import {
  requestViewAccounts,
  requestViewProductListings,
  requestViewBuyerListings,
} from '../../../api/admin/product/admin-product.actions';
import { setInitialFilters, setTab } from '../../../slices/admin/product-management/product-management.slice';

import ProductManagement from '../../../components/screens/admin/product-management/ProductManagement';

const ProductManagementContainer = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [listings, setListings] = useState([]);

  const { classifications, categories, types, productListings, buyerListings } = useSelector(({ product }) => product);
  const { accounts } = useSelector(({ party }) => party);
  const { initialFilters, tab } = useSelector(({ productManagement }) => productManagement);

  const dispatch = useDispatch();

  const retrieveProductListings = useCallback(
    (filters) => {
      dispatch(
        requestViewProductListings({
          filters: {
            classificationExternalReference: filters.productClassification?.externalReference,
            categoryExternalReference: filters.productCategory?.externalReference,
            typeExternalReference: filters.productType?.externalReference,
            recordsPerPage: filters.recordsPerPage.value,
            name: filters.searchString,
            organisationExternalReference: filters.account?.externalReference,
            status: filters.status?.value,
            page: filters.page,
            showFeatured: filters.featuredFilter,
          },
          onSuccess: (totalSellerListings) => {
            setTotalPages(Math.ceil(totalSellerListings / filters.recordsPerPage.value));
            setCurrentPage(filters.page);
          },
        })
      );
      dispatch(setInitialFilters(filters));
    },
    [dispatch]
  );

  const retrieveBuyerListings = useCallback(
    (filters) => {
      dispatch(
        requestViewBuyerListings({
          filters: {
            classificationExternalReference: filters.productClassification?.externalReference,
            categoryExternalReference: filters.productCategory?.externalReference,
            typeExternalReference: filters.productType?.externalReference,
            recordsPerPage: filters.recordsPerPage.value,
            name: filters.searchString,
            organisationExternalReference: filters.account?.externalReference,
            status: filters.status?.value,
            page: filters.page,
            showFeatured: filters.featuredFilter,
          },
          onSuccess: (totalBuyerListings) => {
            setTotalPages(Math.ceil(totalBuyerListings / filters.recordsPerPage.value));
            setCurrentPage(filters.page);
          },
        })
      );
      dispatch(setInitialFilters(filters));
    },
    [dispatch]
  );

  useEffect(() => {
    dispatch(requestViewAllClassifications());
    dispatch(requestViewAccounts());
  }, [dispatch]);

  useEffect(() => {
    if (tab === 'Product Listings') {
      retrieveProductListings(initialFilters);
    } else if (tab === 'Buyer Listings') {
      retrieveBuyerListings(initialFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFilters, tab]);

  useEffect(() => {
    if (tab === 'Product Listings' && productListings) {
      const listings = productListings.map((listing) => ({
        externalReference: listing.productExternalReference,
        status: listing.status,
        account: listing.account,
        productName: listing.brandName ? listing.brandName : 'N/A',
        productType: listing.specification?.name ?? 'N/A',
        listingDate: listing.originalListingDate ? listing.originalListingDate : '',
        featured: Number(listing.featured) || '',
      }));
      setListings(listings);
    } else if (tab === 'Buyer Listings' && buyerListings) {
      const listings = buyerListings.map((listing) => ({
        externalReference: listing.productExternalReference,
        status: listing.status,
        account: listing.account,
        productName: listing.brandName ? listing.brandName : 'N/A',
        productType: listing.specification?.name ?? 'N/A',
        listingDate: listing.originalListingDate ? listing.originalListingDate : '',
      }));
      setListings(listings);
    }
  }, [tab, productListings, buyerListings]);

  const changeTab = (tab) => {
    dispatch(setTab(tab));
  };

  const retrieveCategories = (classificationExternalReference) => {
    dispatch(requestViewAllCategories({ classificationExternalReference }));
  };

  const retrieveTypes = (categoryExternalReference) => {
    dispatch(requestViewAllTypes({ categoryExternalReference }));
  };

  return (
    <ProductManagement
      listings={listings}
      initialFilters={initialFilters}
      accounts={accounts}
      classifications={sortArray(classifications)}
      categories={sortArray(categories, true)}
      types={sortArray(types, true)}
      tab={tab}
      currentPage={currentPage}
      totalPages={totalPages}
      changeTab={changeTab}
      retrieveCategories={retrieveCategories}
      retrieveTypes={retrieveTypes}
      retrieveProductListings={retrieveProductListings}
      retrieveBuyerListings={retrieveBuyerListings}
    />
  );
};

export default ProductManagementContainer;
